/**
 * Wave 78 F1 · SUB 3 — Investimento.
 *
 * JP decisão D1 (PRD §2.6): público vê "a partir de R$ X" quando
 * listing_price_cents preenchido, OU "Sob consulta" quando vazio.
 * Banda P10-P90 + decomposição por buckets ficam ESCONDIDAS do público
 * (gated atrás de token PM no F3 — fora desta wave).
 *
 * Por que não esconder a seção quando `fromCents=null`: porque "sob
 * consulta" é informação útil pro comprador (sinaliza que existe preço,
 * basta entrar em contato) e leva organicamente pro LeadForm.
 *
 * Layout: card único entre Specs e MemorialFlipbook. Tipografia big-number
 * editorial (font-display, tracking-tighter) — mesma família visual que
 * Hero e LiveBand.
 *
 * Estados:
 *   • investmentSummary === null → seção oculta (não chama formatter)
 *   • investmentSummary.fromCents === null → "Sob consulta" + CTA contato
 *   • investmentSummary.fromCents > 0 → big number + subtitle
 */

import type { PublicShowcase } from '../lib/api';

interface Props {
  data: PublicShowcase;
  /**
   * Opcional · scroll anchor pro LeadForm. O App.tsx pode passar uma
   * função que faz scroll suave pro lead form quando o buyer clica
   * "Solicitar proposta detalhada". Default = focus no #lead via hash
   * fragment, que o LeadForm já escuta.
   */
  onContactClick?: () => void;
}

export function Investimento({ data, onContactClick }: Props) {
  const summary = data.investmentSummary ?? null;

  // Backward-compat: API antiga sem o campo → não renderiza nada.
  // Better silent than wrong.
  if (!summary) return null;

  const hasPrice =
    typeof summary.fromCents === 'number' && summary.fromCents > 0;

  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onContactClick) {
      e.preventDefault();
      onContactClick();
      return;
    }
    // Default: smooth scroll to #lead anchor (LeadForm renders one).
    const el = document.getElementById('lead');
    if (el) {
      e.preventDefault();
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      className="relative border-t border-line bg-bg py-24 sm:py-32"
      aria-label="Investimento"
    >
      <div className="mx-auto max-w-4xl px-6 sm:px-12 lg:px-20">
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
          Investimento
        </p>

        {hasPrice ? (
          <>
            {/* Big number block — formatter no backend já entrega
                "A partir de R$ 1.180.000" pronto. Não recalculamos
                no front pra evitar divergência de formatação. */}
            <p className="mt-6 font-display text-5xl font-light leading-[0.95] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
              {summary.label}
            </p>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink-muted">
              Valor de partida. Memorial técnico, ficha completa e
              condições de pagamento são compartilhados com proponentes
              qualificados.
            </p>
          </>
        ) : (
          <>
            <p className="mt-6 font-display text-5xl font-light leading-[0.95] tracking-tighter text-ink sm:text-6xl">
              Sob consulta.
            </p>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink-muted">
              O valor desta obra é compartilhado após uma primeira
              conversa — entendemos perfil, prazo e formato de compra
              antes de propor.
            </p>
          </>
        )}

        <div className="mt-10 flex flex-wrap gap-4 sm:mt-12">
          <a
            href="#lead"
            onClick={handleContactClick}
            className="inline-flex items-center justify-center bg-accent px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-accent/90"
          >
            Solicitar proposta
          </a>
          <a
            href="#lead"
            onClick={handleContactClick}
            className="inline-flex items-center justify-center border border-line bg-transparent px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-ink-muted transition-colors hover:border-ink hover:text-ink"
          >
            Ver memorial técnico
          </a>
        </div>

        {/* Fine print — quem pergunta sobre desconto pra obra de
            R$ 1M+ não merece menos contexto. */}
        <p className="mt-12 max-w-2xl text-xs leading-relaxed text-ink-subtle">
          Memorial técnico inclui plantas, ART/RRT, descritivos de
          acabamentos, fornecedores verificados (com score histórico) e
          relatório de qualidade auditável. Acesso liberado após
          alinhamento inicial.
        </p>
      </div>
    </section>
  );
}
